import React, {
    useMemo, useState
} from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from 'shared/components/ui/dialog';
import {
    Input
} from 'shared/components/ui/input';
import {
    Button
} from 'shared/components/ui/button';
import {
    Loader2, RefreshCw, AlertCircle, Check, Search, Box,
    Brain, Globe, Cog, Code, Database, Hash, GitFork, Workflow, Cpu, X
} from 'lucide-react';
import {
    cn
} from 'shared/lib/utils';
import useResourcePickerData from './useResourcePickerData';
import InstructionPicker from '../opcode-editor/inline-pickers/InstructionPicker';
import ApiPicker from '../opcode-editor/inline-pickers/ApiPicker';
import McpConnectionPicker from '../opcode-editor/inline-pickers/McpConnectionPicker';
import AuthenticateEmail from 'builders/builderChannel/components/AuthenticateEmail';

/**
 * ResourcePicker — single chip primitive used by every step kind to bind
 * to a sub-org resource. Supported kinds:
 *
 *   Service-backed (lives in user's sub-org library):
 *     llm · api · mcp · opcode · collection · channel · assistant
 *
 *   Local-prop (lives on the parent opcode):
 *     definition · flow · step
 *
 * For llm/api/mcp this delegates to the already-built rich pickers
 * (which include edit + create-new flows wired into Library). For the
 * other kinds it renders a unified DS picker dialog with search.
 *
 * Common props:
 *   kind          — one of the supported strings above (required)
 *   value         — current selection. For llm: { unique_name, model_type };
 *                   for everything else: the `id` string.
 *   onChange      — (newValue) => void. For llm passes the same shape; otherwise
 *                   passes the `id` string AND the raw record as the second arg.
 *   placeholder   — chip-empty caption
 *   localItems    — required for definition/flow/step (array from parent opcode)
 *
 * Visual: avatar tile + title + subtitle + badge + Change button.
 */

const TONE_BY_KIND = {
    llm: 'violet',
    api: 'blue',
    mcp: 'orange',
    opcode: 'violet',
    collection: 'rose',
    channel: 'green',
    assistant: 'violet',
    definition: 'gray',
    flow: 'gray',
    step: 'gray'
};

const ICON_BY_KIND = {
    llm: Brain,
    api: Globe,
    mcp: Cog,
    opcode: Code,
    collection: Database,
    channel: Hash,
    assistant: Cpu,
    definition: Box,
    flow: Workflow,
    step: GitFork
};

const KIND_LABEL = {
    llm: 'instruction',
    api: 'API',
    mcp: 'MCP connection',
    opcode: 'opcode',
    collection: 'collection',
    channel: 'channel',
    assistant: 'assistant',
    definition: 'definition',
    flow: 'flow',
    step: 'step'
};

export default function ResourcePicker(props) {
    const {
        kind
    } = props;
    if (kind === 'llm') return <LlmAdapter {...props} />;
    if (kind === 'api') return <ApiAdapter {...props} />;
    if (kind === 'mcp') return <McpAdapter {...props} />;
    return <UnifiedPicker {...props} />;
}

/* ---------- delegated adapters (existing rich pickers) ---------- */

function LlmAdapter({
    value, onChange
}) {
    return <InstructionPicker value={value} onChange={onChange} />;
}

function ApiAdapter({
    value, onChange
}) {
    return (
        <ApiPicker
            apiId={typeof value === 'string' ? value : value?._id}
            onChange={(api) => onChange(api?._id || '', api)}
        />
    );
}

function McpAdapter({
    value, onChange
}) {
    return (
        <McpConnectionPicker
            connectionId={typeof value === 'string' ? value : value?.connection_id}
            onChange={(conn) => onChange(conn?.connection_id || '', conn)}
        />
    );
}

/* ---------- unified DS picker (everything else) ---------- */

function UnifiedPicker({
    kind,
    value,
    onChange,
    placeholder,
    localItems,
    helpText,
    defaultAssistantId
}) {
    const [open, setOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [, setCreatedChannels] = useState([]);
    const [query, setQuery] = useState('');
    const {
        items, loading, error, refetch
    } = useResourcePickerData(kind, {
        localItems
    });

    const selected = items.find((it) => it.id === value);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items;
        return items.filter((it) =>
            (it.label || '').toLowerCase().includes(q) ||
            (it.sublabel || '').toLowerCase().includes(q) ||
            (it.id || '').toLowerCase().includes(q) ||
            (it.badge || '').toLowerCase().includes(q)
        );
    }, [items, query]);

    const Icon = ICON_BY_KIND[kind] || Box;
    const tone = TONE_BY_KIND[kind] || 'gray';
    const kindLabel = KIND_LABEL[kind] || kind;

    function pick(it) {
        onChange(it.id, it.raw);
        setOpen(false);
    }
    function clear() {
        onChange('', null);
    }
    function handleCreated(channel) {
        refetch();
        onChange(channel._id, channel);
        setCreateOpen(false);
        setOpen(false);
    }

    return (
        <div>
            <div className="ds-pick-chip">
                <span className={`ds-pick-chip-icon ds-tone-${tone}`}>
                    <Icon size={15} />
                </span>
                <div className="ds-pick-chip-body">
                    {selected ? (
                        <>
                            <div className="ds-pick-chip-title">{selected.label}</div>
                            <div className="ds-pick-chip-meta">
                                {selected.badge && <span className="ds-badge ds-badge-muted" style={{
                                    marginRight: 6
                                }}>{selected.badge}</span>}
                                {selected.sublabel || (
                                    <span style={{
                                        fontFamily: 'var(--ds-font-mono)'
                                    }}>{selected.id}</span>
                                )}
                            </div>
                        </>
                    ) : value && loading ? (
                        <>
                            <div className="animate-pulse" style={{
                                height: 13, width: 150, borderRadius: 4, background: 'var(--ds-surface-3)'
                            }} />
                            <div className="animate-pulse" style={{
                                height: 10, width: 210, borderRadius: 4, background: 'var(--ds-surface-3)', marginTop: 6
                            }} />
                        </>
                    ) : value ? (
                        <>
                            <div className="ds-pick-chip-title" style={{
                                fontFamily: 'var(--ds-font-mono)'
                            }}>{value}</div>
                            <div className="ds-pick-chip-meta" style={{
                                fontStyle: 'italic'
                            }}>not in your library</div>
                        </>
                    ) : (
                        <div className="ds-pick-chip-meta" style={{
                            fontStyle: 'italic'
                        }}>
                            {placeholder || `No ${kindLabel} selected.`}
                        </div>
                    )}
                </div>
                <div className="ds-pick-chip-actions">
                    <Button variant="outline" size="sm" type="button" onClick={() => setOpen(true)}>
                        Change
                    </Button>
                    {selected && (
                        <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            onClick={clear}
                            title="Clear selection"
                            className="text-muted-foreground hover:text-destructive h-7 w-7 p-0"
                        >
                            <X size={13} />
                        </Button>
                    )}
                </div>
            </div>
            {helpText && <p className="ds-help" style={{
                marginTop: 6
            }}>{helpText}</p>}
            {error && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>{error}</div>
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl ds-picker-dialog">
                    <DialogHeader>
                        <DialogTitle>Pick {/^[aeiouAEIOU]/.test(kindLabel) ? 'an' : 'a'} {kindLabel}</DialogTitle>
                        <DialogDescription>
                            {isLocalKind(kind)
                                ? `Choose from this opcode's declared ${kindLabel}s.`
                                : `Lists every ${kindLabel} in this sub-org's Library.`}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={`Search ${kindLabel}s…`}
                                className="h-8 pl-7 text-[12.5px]"
                            />
                        </div>
                        {!isLocalKind(kind) && (
                            <Button variant="outline" size="sm" onClick={refetch} disabled={loading}>
                                <RefreshCw className={cn('mr-1 h-3 w-3', loading && 'animate-spin')} /> Refresh
                            </Button>
                        )}
                        {kind === 'channel' && (
                            <Button size="sm" onClick={() => setCreateOpen(true)}>
                                + New
                            </Button>
                        )}
                        <span className="text-xs text-muted-foreground">
                            {filtered.length}{filtered.length !== items.length ? ` of ${items.length}` : ''}
                        </span>
                    </div>

                    <div className="max-h-[60vh] overflow-y-auto rounded border">
                        {loading && (
                            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                            </div>
                        )}
                        {!loading && items.length === 0 && (
                            <div className="p-6 text-center text-sm text-muted-foreground">
                                {isLocalKind(kind)
                                    ? `No ${kindLabel}s declared on this opcode yet.`
                                    : `No ${kindLabel}s in this sub-org yet.`}
                            </div>
                        )}
                        {!loading && items.length > 0 && filtered.length === 0 && (
                            <div className="p-6 text-center text-sm text-muted-foreground">
                                No matches for &ldquo;{query}&rdquo;.
                            </div>
                        )}
                        {!loading && filtered.map((it) => {
                            const sel = it.id === value;
                            return (
                                <button
                                    key={it.id}
                                    type="button"
                                    onClick={() => pick(it)}
                                    className={cn('ds-pick-row', sel && 'ds-pick-row-selected')}
                                >
                                    <span className={`ds-pick-row-icon ds-tone-${tone}`}>
                                        <Icon size={14} />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="truncate font-semibold text-sm">{it.label}</span>
                                            {it.badge && (
                                                <span className="ds-badge ds-badge-muted">{it.badge}</span>
                                            )}
                                            {sel && <Check size={13} style={{
                                                color: 'var(--ds-primary)'
                                            }} />}
                                        </div>
                                        {it.sublabel && (
                                            <div className="ds-pick-chip-meta" style={{
                                                marginTop: 2
                                            }}>
                                                {it.sublabel}
                                            </div>
                                        )}
                                        <div style={{
                                            marginTop: 2,
                                            fontSize: 'var(--ds-text-xs)',
                                            color: 'var(--ds-text-muted)',
                                            fontFamily: 'var(--ds-font-mono)'
                                        }}>
                                            {it.id}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </DialogContent>
            </Dialog>

            {kind === 'channel' && (
                <AuthenticateEmail
                    isOpen={createOpen}
                    setOpenEmailModal={setCreateOpen}
                    setEmailChannels={setCreatedChannels}
                    onCreated={handleCreated}
                    defaultAssistantId={defaultAssistantId}
                />
            )}
        </div>
    );
}

function isLocalKind(kind) {
    return kind === 'definition' || kind === 'flow' || kind === 'step';
}
